import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './context/ThemeContext';
import ScrollToTop from './components/ScrollToTop';

// Import public components
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import WebsiteReportPage from './pages/WebsiteReportPage';

// Import admin components
import AdminLayout from './components/admin/AdminLayout';
import LoginPage from './pages/admin/LoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import BlogManagement from './pages/admin/BlogManagement';
import BlogPostEditor from './pages/admin/BlogPostEditor';
import PricingManagement from './pages/admin/PricingManagement';
import NewsletterManagement from './pages/admin/NewsletterManagement';
import EmailManagement from './pages/admin/EmailManagement';
import ThemeManagement from './pages/admin/ThemeManagement';
import FAQManagement from './pages/admin/FAQManagement';
import ServiceManagement from './pages/admin/ServiceManagement';
import TeamManagement from './pages/admin/TeamManagement';
import AuditLogManagement from './pages/admin/AuditLogManagement';
import SettingsManagement from './pages/admin/SettingsManagement';
import AdminProjectsPage from './pages/admin/AdminProjectsPage';
import AdminTestimonialsPage from './pages/admin/AdminTestimonialsPage';
import AboutManagement from './pages/admin/AboutManagement';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <Router>
            <ScrollToTop />
            <div className="min-h-screen bg-gray-50 flex flex-col">
              <Routes>
              {/* Admin Routes */}
              <Route path="/admin/login" element={<LoginPage />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="projects" element={<AdminProjectsPage />} />
                <Route path="testimonials" element={<AdminTestimonialsPage />} />
                <Route path="blog" element={<BlogManagement />} />
                <Route path="blog/new" element={<BlogPostEditor />} />
                <Route path="blog/edit/:id" element={<BlogPostEditor />} />
                <Route path="pricing" element={<PricingManagement />} />
                <Route path="newsletter" element={<NewsletterManagement />} />
                <Route path="contacts" element={<EmailManagement />} />
                <Route path="faqs" element={<FAQManagement />} />
                <Route path="services" element={<ServiceManagement />} />
                <Route path="team" element={<TeamManagement />} />
                <Route path="about" element={<AboutManagement />} />
                <Route path="audit-logs" element={<AuditLogManagement />} />
                <Route path="theme" element={<ThemeManagement />} />
                <Route path="settings" element={<SettingsManagement />} />
              </Route>
              
              {/* Website Audit Report — standalone layout (no global header/footer) */}
              <Route path="/report/:token" element={<WebsiteReportPage />} />

              {/* Public Routes */}
              <Route path="/*" element={
                <>
                  <Header />
                  <main className="flex-grow">
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/blog" element={<BlogPage />} />
                      <Route path="/blog/:slug" element={<BlogPostPage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/faq" element={<FAQPage />} />
                    </Routes>
                  </main>
                  <Footer />
                </>
              } />
            </Routes>
          </div>
        </Router>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
